import React, { memo, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, InputAdornment, SxProps, Theme } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { useAssetTypesWithAssetsQuery } from '../../queries/assets';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setRightColumn } from '../../store/modules/listManager.slice';
import { cleanObjects, getObjects, setObjects } from '../../store/modules/listObject.slice';
import { getLocations } from '../../store/modules/locations.slice';
import { SkipLogicQuestion } from '../../types/formBuilder';
import { AnyQuestion } from '../../types/questions';
import { datascopeTracker } from '../../utils/eventTracker';
import { paths } from '../../utils/navigation';
import { SearchIcon } from '../icons';
import { Ckeditor } from '../uiComponents/ckeditor';
import { InfoAlert } from '../uiComponents/infoAlert';
import { ActivityPeriodTimeSetting } from './activityPeriodTimeSetting';
import { normalizeMeterConfigurations } from './assetsUtils/meterConfigurationsUtils';
import { buildCustomAssetList } from './buildCustomAssetList';
import colors from './colors';
import { DateValidation } from './dateValidation/dateValidation';
import { DateQuestionField } from './dateValidation/types';
import { FileSetting } from './fileSetting';
import { FormBuilderContext } from './FormBuilderProvider';
import { ALL_METADATA_TYPES } from './helper';
import { iconTypes } from './iconTypes';
import { MeterSetting } from './meterSetting';
import { NumberValidation } from './numberValidation';
import { OldLocationsWarningBanner } from './OldLocationsWarningBanner';
import { PhotoSettings } from './photoSettings';
import { questionTypesList } from './questionTypes';
import { TableQuestionBuilder } from './tableQuestionBuilder';
import { TableQuestionMenu } from './tableQuestionMenu';
import { TableQuestionSetting } from './TableQuestionSetting';

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

const customStyles: Record<string, SxProps<Theme>> = {
  searchDataListBtn: {
    borderRadius: '2px',
    borderBottom: '1px solid',
    fontSize: '12px',
  },
  searchDataListBtnIcon: {
    height: '13px',
    width: '13px',
  },
  mobileOnlyAlert: {
    marginBottom: '24px',
  },
  divider: { margin: '15px 0 15px' },
};

const QuestionBuilder = memo(function QuestionBuilder(props: {
  index: number;
  question: any;
  dataSources: { tag: string; value: string }[];
  zoomedIdInTable: string;
  expandedInTable: boolean;
  availableFileUpload: boolean;
  logicQuestions: SkipLogicQuestion[];
  tableLogicQuestions: { [key: string]: SkipLogicQuestion[] };
  paying: boolean;
  onNameChange: (id: string, value: string) => void;
  onDescriptionChange: (id: string, value: string) => void;
  onStaticTextChange: (id: string, value: string) => void;
  onDateModeChange: (id: string, value: string) => void;
  onOptionChange: (id: string, index: number, action: string, value: string) => void;
  onMetadataCodeChange: (id: string, value: string) => void;
  onListChoicesChange: (id: string, index: number, action: string, value: string) => void;
  onZoomedInTableHandler: (id: string) => void;
  onAddInTableHandler: (index: number) => void;
  onDeleteInTableHandler: (id: string) => void;
  onDuplicateInTableHandler: (id: string, index: number) => void;
  onTypeInTableChange: (id: string, type: string) => void;
  onNameInTableChange: (id: string, value: string) => void;
  onDescriptionInTableChange: (id: string, value: string) => void;
  onStaticTextInTableChange: (id: string, value: string) => void;
  onDateModeInTableChange: (id: string, value: string) => void;
  onOptionInTableChange: (id: string, index: number, action: string, value: string) => void;
  onListChoicesInTableChange: (id: string, index: number, action: string, value: string) => void;
  onRequiredInTableChange: (id: string) => void;
  onTextValidationChange: (
    questionId: string,
    validation: 'email' | 'rut-chile' | 'container' | null,
    tableId: string,
  ) => void;
  onNumberPercentageChange: (
    questionId: string,
    show: 'percent' | 'sampling' | null,
    tableId: string,
  ) => void;
  onBarcodeTypeInTableChange: (id: string, value: string) => void;
  onDisableGalleryInTableChange: (id: string, value: string) => void;
  onMultiPhotoInTableChange: (id: string, value: string) => void;
  onExpandedHandler: (table: boolean) => void;
  reorderInTable: (id: string, index: number, action: string, tableId: string) => void;
  numberMetadataInTableSetting: (id: string, tag: string, tableId: string) => void;
  qualityCamOnChange: (id: string, value: string) => void;
  qualityCamInTableChange: (id: string, value: string) => void;
  onFileSettingChange: (id: string, value: number | string[], action: string) => void;
  onFileSettingInTableChange: (
    id: string,
    value: number | string[],
    action: string,
    tableId: string,
  ) => void;
  onActivityPeriodTimeSettingChange: (
    id: string,
    value: string,
    action: string,
    tableId?: string,
  ) => void;
  onActivityPeriodTimeSettingInTableChange: (
    id: string,
    value: string,
    action: string,
    tableId: string,
  ) => void;
  onRichTextChange: (id: string, value: string) => void;
  onRichTextInTableChange: (id: string, value: string, tableId: string) => void;
  onNumberValidationChange: (id: string, action: string, value: string) => void;
  onNumberValidationTableChange: (
    id: string,
    action: string,
    value: string,
    tableId: string,
  ) => void;
  onDateValidationChange: (
    id: string,
    dateQuestionFieldToUpdate: DateQuestionField,
    value: string,
  ) => void;
  onDateValidationBatchChange?: (
    questionId: string,
    dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    tableId?: string,
  ) => void;
  onDateValidationTableBatchChange?: (
    questionIdInTable: string,
    dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    tableId: string,
  ) => void;
  onDateValidationTableChange: (
    id: string,
    dateQuestionFieldToUpdate: DateQuestionField,
    value: string,
    tableId: string,
  ) => void;
  onDateValidationCheckboxTableChange: (id: string, tableId: string) => void;
  onRelatedListChange: (id: string, action: string, value: string) => void;
  onRelatedListInTableChange: (id: string, action: string, value: string, tableId: string) => void;
  onOptionsWithCommentsInTableChange: (tableId: string, questionId: string) => void;
  onObligatoryOptionCommentInTableChange: (
    tableId: string,
    questionId: string,
    index: number,
    reqText: boolean,
    reqImage: boolean,
    reqFinding: boolean,
  ) => void;
  onChecklistSettingsChange: (
    questionId: string,
    disableGallery?: boolean,
    maxImages?: number,
    tableId?: string,
  ) => void;
  onSkipLogicInTableChange: (id: string, tableId: string) => void;
  onSkipSettingInTableChange: (id: string, set: string, value: string, tableId: string) => void;
  onMeterChange: (questionId: string, value: string, tableId?: string) => void;
  onAddMeterConfiguration: (questionId: string, tableId?: string) => void;
  onDeleteMeterConfiguration: (questionId: string, configIndex: number, tableId?: string) => void;
  onMeterConfigurationMeterChange: (
    questionId: string,
    configIndex: number,
    newMeterId: string,
    tableId?: string,
  ) => void;
  onAddMeterConfigurationQuestion: (
    questionId: string,
    configIndex: number,
    tableId?: string,
  ) => void;
  onUpdateMeterConfigurationQuestion: (
    questionId: string,
    configIndex: number,
    questionSlotIndex: number,
    value: string,
    tableId?: string,
  ) => void;
  onDeleteMeterConfigurationQuestion: (
    questionId: string,
    configIndex: number,
    questionSlotIndex: number,
    tableId?: string,
  ) => void;
  onCreateAssetQuestion: (
    tableId: string | undefined,
    type: 'number' | 'select_metadata',
    associationContext?: { parentQuestionId: string; configIndex: number; slotIndex: number },
  ) => void;
}) {
  const {
    question: {
      id,
      name,
      description,
      static_text,
      type,
      dateMode,
      options,
      metadata,
      nestableLocations,
      listChoices,
      questions,
      cameraQuality,
      formats,
      startTime,
      workingTime,
      intervalDefault,
      quantity,
      maximumAudioDurationInSeconds,
      rich_text,
      numberValidation,
      numberValidationRelative,
      upperValue,
      lowerValue,
      related_list,
      list_header,
      separator,
      validationOffset,
      dateValidationType,
      rangeCondition,
      offsetHours,
      offsetHoursToLeft,
      offsetHoursToRight,
      updateData,
      meterConfigurations,
      selectedAssets,
    },
    dataSources,
    zoomedIdInTable,
    expandedInTable,
    availableFileUpload,
    logicQuestions,
    tableLogicQuestions,
    paying,
    onNameChange,
    onDescriptionChange,
    onStaticTextChange,
    onDateModeChange,
    onOptionChange,
    onListChoicesChange,
    onZoomedInTableHandler,
    onAddInTableHandler,
    onDeleteInTableHandler,
    onDuplicateInTableHandler,
    onTypeInTableChange,
    onNameInTableChange,
    onDescriptionInTableChange,
    onStaticTextInTableChange,
    onDateModeInTableChange,
    onOptionInTableChange,
    onListChoicesInTableChange,
    onRequiredInTableChange,
    onTextValidationChange,
    onNumberPercentageChange,
    onBarcodeTypeInTableChange,
    onDisableGalleryInTableChange,
    onMultiPhotoInTableChange,
    onExpandedHandler,
    reorderInTable,
    numberMetadataInTableSetting,
    qualityCamOnChange,
    qualityCamInTableChange,
    onFileSettingChange,
    onFileSettingInTableChange,
    onActivityPeriodTimeSettingChange,
    onActivityPeriodTimeSettingInTableChange,
    onRichTextChange,
    onRichTextInTableChange,
    onNumberValidationChange,
    onNumberValidationTableChange,
    onDateValidationChange,
    onDateValidationTableChange,
    onDateValidationCheckboxTableChange,
    onDateValidationBatchChange,
    onDateValidationTableBatchChange,
    onRelatedListChange,
    onRelatedListInTableChange,
    onOptionsWithCommentsInTableChange,
    onObligatoryOptionCommentInTableChange,
    onChecklistSettingsChange,
    onSkipSettingInTableChange,
    onSkipLogicInTableChange,
    onMeterChange,
    onAddMeterConfiguration,
    onDeleteMeterConfiguration,
    onMeterConfigurationMeterChange,
    onAddMeterConfigurationQuestion,
    onUpdateMeterConfigurationQuestion,
    onDeleteMeterConfigurationQuestion,
    onCreateAssetQuestion,
  } = props;

  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const dateQuestionValidationFlag = checkFlag('DateQuestionValidation');

  const styles: StyleSheet = {
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    warningContainer: {
      alignSelf: 'center',
      backgroundColor: colors.primary,
      color: 'white',
      borderRadius: 9,
      textAlign: 'center',
      fontSize: 12,
      fontWeight: 'bold',
      paddingInline: 5,
      marginBottom: 7,
    },
  };

  const dispatch = useAppDispatch();
  const { isActive: listManagerAllowed, showPanel } = useAppSelector((state) => state.listManager);
  const { data: assetTypesData } = useAssetTypesWithAssetsQuery('');
  const hasCustomAssetSelection =
    (selectedAssets?.assets?.length ?? 0) > 0 || (selectedAssets?.asset_types?.length ?? 0) > 0;

  const listsRequest = async (code: string) => {
    try {
      window.open(`${window.location.origin}${paths.lists.index}?metadata_code=${code}`, '_blank');
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (listManagerAllowed && metadata && type !== 'table') {
      if (metadata === 'locations') {
        dispatch(getLocations({}));
        dispatch(setRightColumn('LOCATIONS'));
      } else if (metadata === 'assets') {
        const assetTypesCount = assetTypesData?.asset_types_with_assets
          ? Object.keys(assetTypesData.asset_types_with_assets).length
          : 0;
        const allAssetTypesSelected =
          assetTypesCount > 0 &&
          (selectedAssets?.asset_types?.length ?? 0) === assetTypesCount &&
          (selectedAssets?.assets?.length ?? 0) === 0;
        if (!hasCustomAssetSelection || allAssetTypesSelected) {
          dispatch(getObjects({ metadataCode: 'assets' }));
        } else if (assetTypesData?.asset_types_with_assets) {
          const objects = buildCustomAssetList(
            assetTypesData.asset_types_with_assets,
            selectedAssets ?? {},
          );
          dispatch(setObjects({ objects }));
        } else {
          dispatch(getObjects({ metadataCode: 'assets' }));
        }
        dispatch(setRightColumn('ASSETS'));
      } else if (metadata === 'nestable_locations') {
        dispatch(setRightColumn(null));
      } else {
        dispatch(getObjects({ metadataCode: metadata }));
        dispatch(setRightColumn('OBJECTS'));
      }
    } else {
      dispatch(setRightColumn(''));
      dispatch(cleanObjects());
    }
  }, [
    dispatch,
    listManagerAllowed,
    metadata,
    showPanel,
    type,
    hasCustomAssetSelection,
    selectedAssets,
    assetTypesData?.asset_types_with_assets,
  ]);

  const dataSourceLabel = useMemo(() => {
    if (nestableLocations) {
      return lang.form_builder.customLocations;
    }
    return dataSources.find((source) => source.value === metadata)?.tag ?? dataSources[0].tag;
  }, [metadata, dataSources, nestableLocations]);

  const { formId } = useParams<{ formId: string }>();
  const { setSearchDataListModalOpen, setListModalQuestionId } = useContext(FormBuilderContext);

  return (
    <div style={styles.mainContainer}>
      {!questionTypesList.includes(type) && (
        <div style={{ ...styles.warningContainer, backgroundColor: colors.red }}>
          {lang.form_builder.deprecatedWarning}
        </div>
      )}
      <OldLocationsWarningBanner selectedMetadata={metadata} />
      <div
        style={{
          width: '65%',
        }}
      >
        <TextField
          variant={'outlined'}
          size={'small'}
          margin={'dense'}
          label={lang.form_builder.title}
          value={name}
          onChange={(e) => onNameChange(id, e.target.value)}
          fullWidth
          required
          error={name ? false : true}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Tooltip
                  title={lang.form_builder.titleDescription}
                  placement={'top'}
                >
                  <InfoOutlinedIcon style={{ fontSize: 14 }} />
                </Tooltip>
              </InputAdornment>
            ),
            style: {
              fontSize: 12,
            },
          }}
          InputLabelProps={{
            style: {
              fontSize: 14,
            },
          }}
          inputProps={{
            maxLength: 255,
          }}
        />
      </div>

      {type === 'static_text' ? (
        <>
          <div>
            {static_text ? (
              <TextField
                variant={'outlined'}
                size={'small'}
                margin={'dense'}
                label={lang.form_builder.instruction}
                value={static_text}
                onChange={(e) => onStaticTextChange(id, e.target.value)}
                fullWidth
                multiline
                InputProps={{
                  style: {
                    fontSize: 12,
                  },
                }}
                InputLabelProps={{
                  style: {
                    fontSize: 14,
                  },
                }}
                inputProps={{
                  maxLength: 1024,
                }}
                rows={4}
              />
            ) : (
              <Ckeditor
                value={rich_text}
                onChange={(value: string) => onRichTextChange(id, value)}
              />
            )}
          </div>
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      ) : (
        <>
          <div>
            <TextField
              variant={'outlined'}
              size={'small'}
              margin={'dense'}
              label={lang.form_builder.instruction}
              value={description ? description : ''}
              onChange={(e) => onDescriptionChange(id, e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Tooltip
                      title={lang.form_builder.instructionDescription}
                      placement={'top'}
                    >
                      <InfoOutlinedIcon style={{ fontSize: 14 }} />
                    </Tooltip>
                  </InputAdornment>
                ),
                style: {
                  fontSize: 12,
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: 14,
                },
              }}
              inputProps={{
                maxLength: 1024,
              }}
            />
          </div>
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}
      {type === 'number' && (
        <>
          <NumberValidation
            questionId={id}
            upperValue={upperValue}
            lowerValue={lowerValue}
            numberValidation={numberValidation}
            numberValidationRelative={numberValidationRelative}
            validationOffset={validationOffset}
            onNumberValidationChange={onNumberValidationChange}
            logicQuestions={logicQuestions}
          />
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}
      {type === 'attachment' && (
        <>
          <FileSetting
            questionId={id}
            formats={formats}
            onFileSettingChange={onFileSettingChange}
            quantity={quantity}
            maximumAudioDurationInSeconds={maximumAudioDurationInSeconds}
          />
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}
      {type === 'date' && (
        <>
          <div>
            <FormLabel
              style={{ fontSize: 12, marginBottom: 7 }}
              component='legend'
            >
              {lang.form_builder.type}
            </FormLabel>
            <RadioGroup
              value={dateMode}
              onChange={(e) => onDateModeChange(id, e.target.value)}
            >
              <FormControlLabel
                value='date'
                control={
                  <Radio
                    size={'small'}
                    style={{ padding: '5px 15px' }}
                    color={'primary'}
                  />
                }
                label={<div style={{ fontSize: 12 }}>{lang.form_builder.date}</div>}
              />
              <FormControlLabel
                value='datetime'
                control={
                  <Radio
                    size={'small'}
                    style={{ padding: '5px 15px' }}
                    color={'primary'}
                  />
                }
                label={<div style={{ fontSize: 12 }}>{lang.form_builder.dateAndTime}</div>}
              />
              <FormControlLabel
                value='time'
                control={
                  <Radio
                    size={'small'}
                    style={{ padding: '5px 15px' }}
                    color={'primary'}
                  />
                }
                label={<div style={{ fontSize: 12 }}>{lang.form_builder.time}</div>}
              />
            </RadioGroup>
          </div>
          <Divider style={{ margin: '15px 0 15px' }} />
          {dateQuestionValidationFlag && (
            <DateValidation
              questionId={id}
              dateMode={dateMode}
              dateValidationType={dateValidationType}
              rangeCondition={rangeCondition}
              offsetHours={offsetHours}
              offsetHoursToLeft={offsetHoursToLeft}
              offsetHoursToRight={offsetHoursToRight}
              onDateValidationChange={onDateValidationChange}
              onDateValidationBatchChange={onDateValidationBatchChange}
            />
          )}
        </>
      )}

      {type === 'barcode' && (
        <Box sx={customStyles.mobileOnlyAlert}>
          <InfoAlert message={lang.form_builder.mobileOnlyAlert} />
        </Box>
      )}

      {[
        'select_option_metadata',
        'select_option_metadata_comments',
        'select_check_metadata',
        'select_number_metadata',
        'select_text_metadata',
        'select_metadata',
        'select_activity_period_time',
      ].includes(type) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <FormLabel
            style={{
              fontSize: 12,
              textAlign: 'center',
            }}
            component='legend'
          >
            {lang.form_builder.dataListSource}
          </FormLabel>
          <Button
            size={'small'}
            sx={customStyles.searchDataListBtn}
            endIcon={<SearchIcon sx={customStyles.searchDataListBtnIcon} />}
            onClick={() => {
              datascopeTracker(
                'select_data_list',
                {
                  form_id: formId,
                },
                'AMP',
              );
              setListModalQuestionId(id);
              setSearchDataListModalOpen(true);
            }}
          >
            {dataSourceLabel}
          </Button>
        </div>
      )}
      {related_list && ['multi_option', 'select_metadata'].includes(type) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            columnGap: 5,
            marginBottom: 5,
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <FormLabel
              style={{
                fontSize: 12,
                textAlign: 'left',
              }}
              component='legend'
            >
              {lang.form_builder.header}
            </FormLabel>
            <TextField
              variant={'outlined'}
              size={'small'}
              margin={'dense'}
              placeholder={lang.form_builder.headerPlaceholder}
              value={list_header}
              error={!list_header?.trim()?.includes(separator)}
              helperText={
                !list_header?.trim()?.includes(separator) ? lang.form_builder.headerWarning : ''
              }
              onChange={(e) => onRelatedListChange(id, 'list_header', e.target.value)}
              onBlur={() => {
                if (!list_header?.trim())
                  onRelatedListChange(id, 'list_header', lang.form_builder.headerDefault);
              }}
              fullWidth
              InputProps={{
                style: {
                  fontSize: 12,
                },
              }}
            />
          </div>
          <div>
            <FormLabel
              style={{
                fontSize: 12,
                textAlign: 'left',
              }}
              component='legend'
            >
              {lang.form_builder.separator}
            </FormLabel>
            <TextField
              variant={'outlined'}
              size={'small'}
              value={separator}
              error={!separator?.trim()}
              helperText={!separator?.trim() ? lang.form_builder.separatorWarning : ''}
              margin={'dense'}
              style={{ width: 80 }}
              InputProps={{
                style: {
                  fontSize: 12,
                },
              }}
              inputProps={{
                maxLength: 1,
                style: { textAlign: 'center' },
              }}
              FormHelperTextProps={{ style: { fontSize: 10, margin: 5 } }}
              onChange={(e) => onRelatedListChange(id, 'separator', e.target.value)}
              onBlur={() => {
                if (!separator?.trim()) onRelatedListChange(id, 'separator', '|');
              }}
            />
          </div>
        </div>
      )}
      {ALL_METADATA_TYPES.includes(type) && !nestableLocations && (
        <>
          <div>
            {metadata ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 10,
                }}
              >
                <Button
                  onClick={() => listsRequest(metadata)}
                  variant='outlined'
                  size={'small'}
                  style={{
                    backgroundColor: colors.primary,
                    color: 'white',

                    height: 30,
                  }}
                >
                  <div style={{ textTransform: 'capitalize' }}>
                    {lang.form_builder.changeDataList}
                  </div>
                </Button>
              </div>
            ) : (
              <div style={{ marginTop: 10, alignItems: 'center' }}>
                <FormLabel
                  style={{
                    fontSize: 12,
                    textAlign: 'center',
                  }}
                  component='legend'
                >
                  {lang.form_builder.listValues}
                </FormLabel>

                {listChoices && (
                  <div
                    style={{
                      flexDirection: 'column',
                    }}
                  >
                    {listChoices.map((choice: string[], index: number) => (
                      <div
                        key={`${index}-choices`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <TextField
                          variant={'outlined'}
                          size={'small'}
                          margin={'dense'}
                          placeholder={`${lang.form_builder.alternative} ${index + 1}`}
                          value={choice}
                          onChange={(e) => onListChoicesChange(id, index, 'update', e.target.value)}
                          fullWidth
                          InputProps={{
                            style: {
                              fontSize: 12,
                            },
                          }}
                          inputProps={{
                            maxLength: 1024,
                          }}
                        />

                        <div
                          style={{ cursor: 'pointer', marginLeft: 10 }}
                          onClick={() => onListChoicesChange(id, index, 'delete', 'default')}
                        >
                          <Tooltip
                            title={lang.form_builder.deleteAlternative}
                            placement={'right-end'}
                          >
                            <CloseRoundedIcon
                              fontSize={'small'}
                              style={{
                                fontSize: 17,
                                color: colors.darkGrey,
                                paddingTop: 9,
                              }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  style={{
                    margin: 5,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      onListChoicesChange(
                        id,
                        -1,
                        'create',
                        `${lang.form_builder.alternative} ${
                          listChoices ? listChoices.length + 1 : 1
                        }`,
                      )
                    }
                  >
                    <AddRoundedIcon style={{ fontSize: 20, color: '#2196f3' }} />
                    <FormLabel
                      style={{
                        fontSize: 12,
                        textAlign: 'center',
                        color: '#2196f3',
                      }}
                      component='legend'
                    >
                      {lang.form_builder.addListValue}
                    </FormLabel>
                  </div>
                </div>
              </div>
            )}
          </div>
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}
      {type === 'select_activity_period_time' && (
        <>
          <ActivityPeriodTimeSetting
            questionId={id}
            onActivityPeriodTimeSettingChange={onActivityPeriodTimeSettingChange}
            startTime={startTime}
            workingTime={workingTime}
            intervalDefault={intervalDefault}
          />
          <Divider sx={customStyles.divider} />
        </>
      )}
      {(Boolean(meterConfigurations?.length) || updateData === '1') &&
        type === 'select_metadata' && (
          <>
            <MeterSetting
              questionId={id}
              meterConfigurations={normalizeMeterConfigurations(props.question)}
              selectedAssets={selectedAssets}
              onAddMeterConfiguration={onAddMeterConfiguration}
              onDeleteMeterConfiguration={onDeleteMeterConfiguration}
              onMeterConfigurationMeterChange={onMeterConfigurationMeterChange}
              onAddMeterConfigurationQuestion={onAddMeterConfigurationQuestion}
              onUpdateMeterConfigurationQuestion={onUpdateMeterConfigurationQuestion}
              onDeleteMeterConfigurationQuestion={onDeleteMeterConfigurationQuestion}
              onCreateAssetQuestion={onCreateAssetQuestion}
            />
            <Divider sx={customStyles.divider} />
          </>
        )}
      {['multi_option', 'select_option_metadata', 'select_option_metadata_comments'].includes(
        type,
      ) && (
        <>
          <div style={{ alignItems: 'center', marginTop: 10 }}>
            <FormLabel
              style={{
                fontSize: 12,
                textAlign: 'center',
              }}
              component='legend'
            >
              {lang.form_builder.alternativeColumns}
            </FormLabel>
            <div
              style={{
                flexDirection: 'column',
              }}
            >
              {options &&
                options.map((option: any, index: number) => (
                  <div
                    key={`${index}-options`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      variant={'outlined'}
                      size={'small'}
                      margin={'dense'}
                      placeholder={`${lang.form_builder.alternative} ${index + 1}`}
                      value={type === 'select_option_metadata_comments' ? option.option : option}
                      onChange={(e) => onOptionChange(id, index, 'update', e.target.value)}
                      fullWidth
                      InputProps={{
                        style: {
                          fontSize: 12,
                        },
                      }}
                      inputProps={{
                        maxLength: 1024,
                      }}
                    />
                    <div
                      style={{ cursor: 'pointer', marginLeft: 10 }}
                      onClick={() => onOptionChange(id, index, 'delete', 'default')}
                    >
                      <Tooltip
                        title={lang.form_builder.deleteAlternative}
                        placement={'right-end'}
                      >
                        <CloseRoundedIcon
                          fontSize={'small'}
                          style={{
                            fontSize: 17,
                            color: colors.darkGrey,
                            paddingTop: 9,
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ))}
            </div>
            <div
              style={{
                margin: 5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => onOptionChange(id, -1, 'create', lang.form_builder.newOption)}
              >
                <AddRoundedIcon style={{ fontSize: 20, color: '#2196f3' }} />
                <FormLabel
                  style={{
                    fontSize: 12,
                    textAlign: 'center',
                    color: '#2196f3',
                  }}
                  component='legend'
                >
                  {lang.form_builder.addOption}
                </FormLabel>
              </div>
            </div>
          </div>
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}

      {type === 'table' && (
        <>
          <div>
            {questions.map((q: AnyQuestion, idx: number) => {
              return (
                <div
                  key={q.id}
                  style={{ padding: '7px 1px' }}
                >
                  {zoomedIdInTable === q.id ? (
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <Card
                        id={q.id}
                        elevation={5}
                        style={{ padding: '0 10px 0 2px', flex: 1 }}
                      >
                        <CardContent style={{ padding: '16px 16px 4px' }}>
                          <TableQuestionBuilder
                            index={idx}
                            tableId={id}
                            question={q}
                            dataSources={dataSources}
                            onNameInTableChange={onNameInTableChange}
                            onDescriptionInTableChange={onDescriptionInTableChange}
                            onStaticTextInTableChange={onStaticTextInTableChange}
                            onDateModeInTableChange={onDateModeInTableChange}
                            onOptionInTableChange={onOptionInTableChange}
                            onListChoicesInTableChange={onListChoicesInTableChange}
                            qualityCamOnChange={qualityCamInTableChange}
                            paying={paying}
                            onFileSettingChange={onFileSettingInTableChange}
                            onActivityPeriodTimeSettingChange={
                              onActivityPeriodTimeSettingInTableChange
                            }
                            onRichTextChange={onRichTextInTableChange}
                            onRelatedListInTableChange={onRelatedListInTableChange}
                            onNumberValidationTableChange={onNumberValidationTableChange}
                            onDateValidationTableChange={onDateValidationTableChange}
                            onDateValidationTableBatchChange={onDateValidationTableBatchChange}
                            onAddMeterConfiguration={onAddMeterConfiguration}
                            onDeleteMeterConfiguration={onDeleteMeterConfiguration}
                            onMeterConfigurationMeterChange={onMeterConfigurationMeterChange}
                            onAddMeterConfigurationQuestion={onAddMeterConfigurationQuestion}
                            onUpdateMeterConfigurationQuestion={onUpdateMeterConfigurationQuestion}
                            onDeleteMeterConfigurationQuestion={onDeleteMeterConfigurationQuestion}
                            onCreateAssetQuestion={onCreateAssetQuestion}
                            logicQuestions={tableLogicQuestions[id]}
                          />
                          <TableQuestionMenu
                            tableId={id}
                            index={idx}
                            question={q}
                            onAddInTableHandler={onAddInTableHandler}
                            onDeleteInTableHandler={onDeleteInTableHandler}
                            onDuplicateInTableHandler={onDuplicateInTableHandler}
                            onTypeInTableChange={onTypeInTableChange}
                            disableDelete={questions.length === 1}
                            availableFileUpload={availableFileUpload}
                          />
                        </CardContent>
                        {[
                          'text',
                          'number',
                          'date',
                          'signature',
                          'barcode',
                          'photo',
                          'multi_photo',
                          'multi_option',
                          'select_metadata',
                          'select_number_metadata',
                          'select_text_metadata',
                          'select_option_metadata',
                          'select_option_metadata_comments',
                          'attachment',
                          'select_activity_period_time',
                        ].includes(q.type) && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              fontSize: 12,
                              justifyContent: 'center',
                            }}
                            onClick={() => onExpandedHandler(true)}
                          >
                            {expandedInTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            {lang.form_builder.advanced}
                          </div>
                        )}
                        {expandedInTable && (
                          <TableQuestionSetting
                            tableId={id}
                            question={q}
                            onRequiredInTableChange={onRequiredInTableChange}
                            onBarcodeTypeInTableChange={onBarcodeTypeInTableChange}
                            onDisableGalleryInTableChange={onDisableGalleryInTableChange}
                            onMultiPhotoInTableChange={onMultiPhotoInTableChange}
                            numberMetadataInTableSetting={numberMetadataInTableSetting}
                            onRelatedListInTableChange={onRelatedListInTableChange}
                            onOptionsWithCommentsInTableChange={onOptionsWithCommentsInTableChange}
                            onTextValidationChange={onTextValidationChange}
                            onNumberPercentageChange={onNumberPercentageChange}
                            onObligatoryOptionCommentInTableChange={
                              onObligatoryOptionCommentInTableChange
                            }
                            onChecklistSettingsChange={onChecklistSettingsChange}
                            tableLogicQuestions={tableLogicQuestions}
                            onSkipSettingInTableChange={onSkipSettingInTableChange}
                            paying={paying}
                            onSkipLogicInTableChange={onSkipLogicInTableChange}
                            onMeterChange={onMeterChange}
                            onDateValidationCheckboxTableChange={
                              onDateValidationCheckboxTableChange
                            }
                          />
                        )}
                      </Card>
                      <div
                        style={{
                          position: 'absolute',
                          right: 20,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignSelf: 'center',
                        }}
                      >
                        {idx !== 0 && (
                          <Tooltip
                            title={lang.form_builder.moveUp}
                            placement={'right'}
                          >
                            <ArrowDropUpIcon
                              style={{ cursor: 'pointer' }}
                              onClick={() => reorderInTable(q.id, idx, 'up', id)}
                            />
                          </Tooltip>
                        )}
                        {idx !== questions.length - 1 && (
                          <Tooltip
                            title={lang.form_builder.moveDown}
                            placement={'right'}
                          >
                            <ArrowDropDownIcon
                              style={{ cursor: 'pointer' }}
                              onClick={() => reorderInTable(q.id, idx, 'down', id)}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Card
                      id={q.id}
                      elevation={3}
                      onClick={() => onZoomedInTableHandler(q.id)}
                      style={{
                        borderStyle: 'solid',
                        borderWidth: 1,
                        borderColor: colors.trans,
                      }}
                    >
                      <CardContent>
                        <div
                          style={{
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {
                            iconTypes(15, 7, colors.primary)[
                              questionTypesList.includes(q.type) ? q.type : 'deprecated'
                            ]
                          }
                          {lang.types[questionTypesList.includes(q.type) ? q.type : 'deprecated']}
                        </div>
                        <div style={{ textAlign: 'left', paddingLeft: 13 }}>{q.name}</div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })}
          </div>
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}
      {(type === 'photo' || type === 'multi_photo') && (
        <>
          <PhotoSettings
            questionId={id}
            paying={paying}
            cameraQuality={cameraQuality}
            qualityCamOnChange={qualityCamOnChange}
          />{' '}
          <Divider style={{ margin: '15px 0 15px' }} />
        </>
      )}
    </div>
  );
});

export { QuestionBuilder };
